import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { t } from '@/i18n/ro';
import {
  getProjectRoles,
  updateProjectMemberRoles,
  type ProjectRole,
} from '@/services/projects.service';
import {
  Button,
  Modal,
} from '@/components/common';
import type { Member } from '@/types';

interface EditMemberRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  member: Member | null;
  onRolesUpdated: () => void;
}

export function EditMemberRolesModal({
  isOpen,
  onClose,
  projectId,
  member,
  onRolesUpdated,
}: EditMemberRolesModalProps) {
  const [roles, setRoles] = useState<ProjectRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const principal = member?._embedded?.principal;
  const memberName = principal
    ? `${principal.firstName || ''} ${principal.lastName || ''}`.trim() || principal.login
    : member?._links?.principal?.title || '';

  // Încarcă rolurile disponibile și preselectează rolurile curente ale membrului
  useEffect(() => {
    if (!isOpen || !member) return;

    setIsLoadingData(true);
    setError(null);
    // Prefill cu rolurile curente ale membrului
    const currentRoleIds = (member._embedded?.roles || [])
      .map((r) => r.id)
      .filter((id): id is number => typeof id === 'number');
    setSelectedRoleIds(currentRoleIds);

    getProjectRoles()
      .then((rolesData) => {
        const filteredRoles = rolesData.filter(
          (role) => role.name === 'Admin proiect' || role.name === 'Membru'
        );
        setRoles(filteredRoles);
      })
      .catch((err) => {
        console.error('Failed to load roles:', err);
        setError('Nu s-au putut încărca rolurile.');
      })
      .finally(() => setIsLoadingData(false));
  }, [isOpen, member]);

  const handleRoleToggle = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSubmit = async () => {
    if (!member) return;
    if (selectedRoleIds.length === 0) {
      setError('Selectați cel puțin un rol.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await updateProjectMemberRoles(projectId, Number(member.id), selectedRoleIds);
      onRolesUpdated();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to update member roles:', err);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Nu s-au putut modifica rolurile.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Modifică rolurile membrului"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={isLoadingData || selectedRoleIds.length === 0}
            leftIcon={<Pencil className="w-4 h-4" />}
          >
            Salvează
          </Button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
          {error}
        </div>
      )}

      {isLoadingData ? (
        <div className="py-8 text-center text-gray-500">
          Se încarcă...
        </div>
      ) : (
        <div className="space-y-6">
          {memberName && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Membru
              </label>
              <p className="text-sm text-gray-900 font-medium">{memberName}</p>
            </div>
          )}

          {/* Selectare roluri */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roluri <span className="text-danger-500">*</span>
            </label>
            <div className="space-y-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {role.name}
                  </span>
                </label>
              ))}
            </div>
            {roles.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Nu există roluri disponibile.
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
